/**
 * Presence Service - Handles user online/offline status tracking
 * Uses Firebase Realtime Database presence features
 */

import { database } from './firebase';
import {
  ref,
  set,
  onValue,
  onDisconnect,
  serverTimestamp,
  get,
  update
} from 'firebase/database';
import { getCurrentTimestamp, formatDateTimeCT } from '../utils/timeUtils';

/**
 * Sets up presence tracking for a user
 * This should be called when the user logs in
 *
 * @param {string} userId - User ID
 * @param {Object} userData - Additional user data (displayName, photoURL)
 * @returns {Promise<void>}
 */
export const setupPresence = async (userId, userData) => {
  try {
    const userPresenceRef = ref(database, `presence/${userId}`);
    const userRef = ref(database, `users/${userId}`);

    // Get current timestamp for lastOnline
    const timestamp = getCurrentTimestamp();
    const formattedDate = formatDateTimeCT(timestamp);

    // Set user as online
    const presenceData = {
      online: true,
      lastSeen: serverTimestamp(),
      displayName: userData.displayName || '',
      photoURL: userData.photoURL || '',
      currentRoom: null,
      updatedAt: serverTimestamp()
    };

    await set(userPresenceRef, presenceData);

    // Update lastOnline in users table
    await update(userRef, {
      lastOnline: formattedDate,
      lastOnlineTimestamp: timestamp
    });

    // Set up automatic offline status on disconnect
    onDisconnect(userPresenceRef).set({
      online: false,
      lastSeen: serverTimestamp(),
      displayName: userData.displayName || '',
      photoURL: userData.photoURL || '',
      currentRoom: null
    });

    console.log(`Presence set up for user: ${userId}`);
  } catch (error) {
    console.error('Error setting up presence:', error);
    throw error;
  }
};

/**
 * Manually sets user status to offline
 * This should be called when the user logs out
 *
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const setOffline = async (userId) => {
  try {
    const userPresenceRef = ref(database, `presence/${userId}`);
    await set(userPresenceRef, {
      online: false,
      lastSeen: serverTimestamp(),
      currentRoom: null
    });

    console.log(`User ${userId} set to offline`);
  } catch (error) {
    console.error('Error setting offline status:', error);
  }
};

/**
 * Updates the current room a user is in
 *
 * @param {string} userId - User ID
 * @param {string|null} roomCode - Room code or null if leaving all rooms
 * @returns {Promise<void>}
 */
export const updateCurrentRoom = async (userId, roomCode) => {
  try {
    const userPresenceRef = ref(database, `presence/${userId}`);
    const snapshot = await get(userPresenceRef);

    if (snapshot.exists()) {
      const currentData = snapshot.val();
      await set(userPresenceRef, {
        ...currentData,
        currentRoom: roomCode,
        updatedAt: serverTimestamp()
      });

      console.log(`User ${userId} current room updated to: ${roomCode || 'none'}`);
    }
  } catch (error) {
    console.error('Error updating current room:', error);
  }
};

/**
 * Gets the current online status of a user
 *
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Presence data or null if not found
 */
export const getUserPresence = async (userId) => {
  try {
    const userPresenceRef = ref(database, `presence/${userId}`);
    const snapshot = await get(userPresenceRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }

    return null;
  } catch (error) {
    console.error('Error getting user presence:', error);
    return null;
  }
};

/**
 * Subscribes to real-time presence updates for a specific user
 *
 * @param {string} userId - User ID to monitor
 * @param {Function} callback - Callback function with presence data
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserPresence = (userId, callback) => {
  const userPresenceRef = ref(database, `presence/${userId}`);

  return onValue(userPresenceRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.val());
    } else {
      callback({ online: false, lastSeen: null });
    }
  });
};

/**
 * Subscribes to presence updates for multiple users
 *
 * @param {Array<string>} userIds - Array of user IDs to monitor
 * @param {Function} callback - Callback function with map of userId -> presence data
 * @returns {Function} Unsubscribe function
 */
export const subscribeToMultiplePresences = (userIds, callback) => {
  const unsubscribers = [];
  const presenceMap = {};

  userIds.forEach((userId) => {
    const userPresenceRef = ref(database, `presence/${userId}`);

    const unsubscribe = onValue(userPresenceRef, (snapshot) => {
      if (snapshot.exists()) {
        presenceMap[userId] = snapshot.val();
      } else {
        presenceMap[userId] = { online: false, lastSeen: null };
      }

      // Call callback with updated presence map
      callback({ ...presenceMap });
    });

    unsubscribers.push(unsubscribe);
  });

  // Return function to unsubscribe from all
  return () => {
    unsubscribers.forEach(unsub => unsub());
  };
};

/**
 * Gets presence status for multiple users at once
 *
 * @param {Array<string>} userIds - Array of user IDs
 * @returns {Promise<Object>} Map of userId -> presence data
 */
export const getMultiplePresences = async (userIds) => {
  try {
    const presenceMap = {};

    await Promise.all(
      userIds.map(async (userId) => {
        const presence = await getUserPresence(userId);
        presenceMap[userId] = presence || { online: false, lastSeen: null };
      })
    );

    return presenceMap;
  } catch (error) {
    console.error('Error getting multiple presences:', error);
    return {};
  }
};

/**
 * Formats the last seen time for display
 *
 * @param {number|null} lastSeen - Timestamp of last seen
 * @returns {string} Formatted string
 */
export const formatLastSeen = (lastSeen) => {
  if (!lastSeen) return 'Never';

  const now = Date.now();
  const diff = now - lastSeen;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(lastSeen).toLocaleDateString();
};
